<template>
  <div class="approval-process">
    <div class="approval-process__head">
      <span class="approval-process__title">审批操作</span>
    </div>
    <el-table
      v-loading="loading"
      :data="rows"
      row-key="rowKey"
      :tree-props="{ children: 'children' }"
      :border="false"
      :empty-text="errorMsg ? '' : '暂无流程节点'"
      style="width: 100%"
    >
      <el-table-column label="节点" min-width="180">
        <template slot-scope="scope">
          <span>{{ scope.row.processName }}</span>
          <span v-if="scope.row.taskType" class="approval-process__type">{{ scope.row.taskType }}</span>
        </template>
      </el-table-column>
      <el-table-column label="处理对象" width="140">
        <template slot-scope="scope">{{ scope.row.staffName || '-' }}</template>
      </el-table-column>
      <el-table-column label="操作时间" prop="approvalTime" width="180" sortable>
        <template slot-scope="scope">{{ scope.row.approvalTime || '-' }}</template>
      </el-table-column>
      <el-table-column label="审批状态" width="100">
        <template slot-scope="scope">
          <div v-if="scope.row.approvalState" :class="stateClass(scope.row.approvalState)">
            {{ stateText(scope.row.approvalState) }}
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="审批原因" min-width="180" show-overflow-tooltip>
        <template slot-scope="scope">{{ suggestText(scope.row) }}</template>
      </el-table-column>
    </el-table>
    <p v-if="errorMsg" class="approval-process__error">{{ errorMsg }}</p>
    <div v-if="hasDiagram" class="approval-process__diagram">
      <div class="approval-process__diagram-head">
        <span class="approval-process__diagram-title">审批流程</span>
        <el-button type="text" size="mini" @click="zoomVisible = true">放大</el-button>
      </div>
      <look-bpmn
        ref="diagram"
        :deployment-id="queryDeploymentId"
        :process-instance-id="queryInstanceId"
        :nodes="diagramNodes"
        :height="300"
      />
    </div>
    <el-dialog title="审批流程" :visible.sync="zoomVisible" width="96%" append-to-body>
      <look-bpmn
        v-if="zoomVisible"
        :deployment-id="queryDeploymentId"
        :process-instance-id="queryInstanceId"
        :nodes="diagramNodes"
        :height="240"
        content-fit
      />
    </el-dialog>
  </div>
</template>

<script>
import serverConfig from '@/assets/global/serverConfig';
import LookBpmn from './components/LookBpmn.vue';

// 与旧 iframe 表格、以及图上的状态文案保持同一口径
const STATE_TEXT = { A: '审批中', Y: '已通过', R: '已驳回' };

export default {
  name: 'ApprovalProcess',
  components: { LookBpmn },
  props: {
    /** 流程部署 ID；为空时取 approveVO 或按流程编码反查 */
    deploymentId: { type: String, default: '' },
    /** 流程实例 ID；无实例时展示流程定义预览 */
    processInstanceId: { type: String, default: '' },
    /** 流程定义 ID，可选 */
    definitionId: { type: String, default: '' },
    /** 流程编码，用于无部署信息时反查部署 ID */
    flowType: { type: String, default: '' },
    /**
     * 审批主表信息，含 camundaDeploymentId / camundaInstanceId。
     * 宿主模板请用 camelCase 绑定（:approveVO），Vue 会把它转成 approve-v-o，写 :approve-vo 绑不上。
     */
    approveVO: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      errorMsg: '',
      rows: [],
      nodes: [],
      deploymentOverride: '',
      instanceOverride: '',
      resolvedDeploymentId: '',
      zoomVisible: false
    };
  },
  computed: {
    /** 图上使用的部署 ID：显式属性 → 反查结果 → approveVO */
    queryDeploymentId() {
      const approveVO = this.approveVO || {};
      return this.deploymentOverride || this.deploymentId || this.resolvedDeploymentId || approveVO.camundaDeploymentId || '';
    },
    /** 图上使用的实例 ID */
    queryInstanceId() {
      const approveVO = this.approveVO || {};
      return this.instanceOverride || this.processInstanceId || approveVO.camundaInstanceId || '';
    },
    hasDiagram() {
      // 仅有实例 ID 时拿不到真实流程定义，画出来会是兜底示意，与表格对不上
      return !!this.queryDeploymentId;
    },
    /** 表格行 → 图上的状态（按 activityId 匹配） */
    diagramNodes() {
      return this.rows.map((row) => ({
        activityId: row.rowKey,
        processName: row.processName,
        staffName: this.diagramStaffName(row),
        approvalState: row.approvalState
      }));
    }
  },
  watch: {
    deploymentId() {
      this.load();
    },
    processInstanceId() {
      this.load();
    },
    approveVO: {
      deep: true,
      handler() {
        this.load();
      }
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    /** 拉取节点数据并按接口返回顺序组装表格行 */
    async load() {
      this.errorMsg = '';
      this.rows = [];
      const query = this.buildQuery();
      if (!query.deploymentId && this.flowType) {
        query.deploymentId = await this.resolveDeploymentId(this.flowType);
      }
      this.resolvedDeploymentId = query.deploymentId || '';
      if (!query.deploymentId && !query.processInstanceId) {
        this.nodes = [];
        return;
      }
      this.loading = true;
      try {
        const res = await this.api.postJson('/camunda-server/process-definition/get-process-nodes', query);
        const body = res && res.data ? res.data : {};
        if (body.code !== serverConfig.REQUIRE_SUCCESS_CODE) {
          this.errorMsg = body.msg || '流程节点加载失败';
          return;
        }
        this.nodes = Array.isArray(body.data) ? body.data : [];
        this.rows = this.normalize(this.nodes);
        this.$emit('getProcessSuccess', this.rows);
      } catch {
        this.errorMsg = '流程节点加载失败';
      } finally {
        this.loading = false;
      }
    },
    /** 入参优先级：显式属性 → approveVO */
    buildQuery() {
      const approveVO = this.approveVO || {};
      return {
        deploymentId: this.deploymentOverride || this.deploymentId || approveVO.camundaDeploymentId || '',
        processInstanceId: this.instanceOverride || this.processInstanceId || approveVO.camundaInstanceId || '',
        definitionId: this.definitionId || ''
      };
    },
    /** 无部署信息时按流程编码反查部署 ID */
    async resolveDeploymentId(flowType) {
      let tenantGuid = '';
      let staffGuid = '';
      try {
        tenantGuid = localStorage.getItem('guid') || '';
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        staffGuid = userInfo.staffGuid || '';
      } catch {
        // 本地存储不可用时按空值请求，由接口返回明确错误
      }
      try {
        const res = await this.api.postJson('/ms-sales-perform-service/work-flow/data/get-camunda-deployment-id', { tenantGuid, flowType, staffGuid });
        const body = res && res.data ? res.data : {};
        return body.code === serverConfig.REQUIRE_SUCCESS_CODE ? body.data : '';
      } catch {
        return '';
      }
    },
    /**
     * 节点 → 表格行。
     * 多成员节点展开为子行；无审批人时按组织 → 岗位 → 候选人回退显示。
     * @param {Array} nodes 接口返回的节点列表。
     * @returns {Array} 表格行。
     */
    normalize(nodes) {
      return (nodes || []).map((node) => {
        const row = {
          rowKey: node.activityId,
          processName: node.processName,
          taskType: node.taskType || '',
          staffName: '',
          approvalState: '',
          approvalTime: '',
          approvalSuggest: ''
        };
        const staff = Array.isArray(node.staffVOS) ? node.staffVOS : [];
        if (staff.length > 1) {
          const latest = staff.filter((item) => item.approvalState).slice(-1)[0];
          if (latest) Object.assign(row, this.pickStaff(latest));
          row.staffName = `共${staff.length}人`;
          row.children = staff.map((item) => this.toMemberRow(node, item));
          return row;
        }
        if (staff.length === 1) {
          Object.assign(row, this.pickStaff(staff[0]));
          return row;
        }
        const orgs = Array.isArray(node.organisationVOS) ? node.organisationVOS : [];
        if (orgs.length) {
          row.staffName = orgs.length > 1 ? `${orgs[0].organisationName}等${orgs.length}个部门` : orgs[0].organisationName;
          return row;
        }
        const positions = Array.isArray(node.positionVOS) ? node.positionVOS : [];
        if (positions.length) {
          row.staffName = positions.length > 1 ? `${positions[0].positionName}等${positions.length}个岗位` : positions[0].positionName;
          return row;
        }
        const candidates = Array.isArray(node.candidateUsers) ? node.candidateUsers : [];
        if (candidates.length) {
          row.staffName = candidates.length > 1 ? `${candidates[0].staffName}等${candidates.length}个人员` : candidates[0].staffName;
          if (candidates.length > 1) row.children = candidates.map((item) => this.toMemberRow(node, item));
        }
        return row;
      });
    },
    /** 成员审批信息 → 行上的四个字段 */
    pickStaff(member) {
      return {
        staffName: member.staffName || '',
        approvalState: member.approvalState || '',
        approvalTime: member.approvalTime || '',
        approvalSuggest: member.approvalSuggest || ''
      };
    },
    /** 成员 → 子行 */
    toMemberRow(node, member) {
      return {
        rowKey: `${node.activityId}-${member.staffGuid || member.staffName}`,
        processName: node.processName,
        taskType: '',
        ...this.pickStaff(member)
      };
    },
    /** 图上处理人：多成员展开成员姓名，其余沿用表格文案 */
    diagramStaffName(row) {
      if (!row.children || !row.children.length) return row.staffName;
      return row.children.map((member) => member.staffName).join('、');
    },
    stateText(state) {
      return STATE_TEXT[state] || '-';
    },
    /** 状态胶囊样式类，沿用 heart2 同名类，便于两端对照 */
    stateClass(state) {
      return { A: 'state-sty', Y: 'state-sty1', R: 'state-sty2' }[state] || '';
    },
    suggestText(row) {
      if (row.approvalSuggest) return row.approvalSuggest;
      return row.processName === '发起人' ? '无' : '-';
    },
    /** 供宿主页面按指定参数重新渲染 */
    renderProcessNodes(param = {}) {
      if (param.deploymentId) this.deploymentOverride = param.deploymentId;
      if (param.processInstanceId) this.instanceOverride = param.processInstanceId;
      return this.load();
    },
    /** 当前表格行，便于宿主页面复用 */
    getProcessNodes() {
      return this.rows;
    }
  }
};
</script>

<style lang="less" scoped>
.approval-process {
  // 区域标题与页面 .form-title 同款（16px / 700 / 40px 行高）；宿主容器可能带居中，
  // 标题必须靠左
  &__head {
    height: 40px;
    line-height: 40px;
    text-align: left;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
  }

  &__type {
    margin-left: 6px;
    padding: 0 6px;
    font-size: 12px;
    line-height: 18px;
    color: #4fa1a4;
    background: #e3f0f0;
    border-radius: 2px;
  }

  // 状态胶囊：与 heart2 ApprovalProcess 同名同值
  .state-sty,
  .state-sty1,
  .state-sty2 {
    width: 52px;
    height: 21px;
    font-size: 12px;
    line-height: 21px;
    text-align: center;
    border-radius: 3px;
  }

  .state-sty {
    background: #ffeee3;
    color: #ff8623;
  }

  .state-sty1 {
    background: #e3f0f0;
    color: #4fa1a4;
  }

  .state-sty2 {
    background: #ffe4e2;
    color: #fe4638;
  }

  &__diagram {
    margin-top: 12px;
  }

  &__diagram-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__diagram-title {
    font-size: 16px;
    font-weight: 700;
    color: #212121;
  }

  &__empty,
  &__error {
    padding: 16px 0;
    font-size: 13px;
    text-align: center;
    color: #9a9a9a;
  }

  &__error {
    color: #fe4638;
  }

  // 表格对齐 FBT 全局标准（style.css 的 .content-main-box 口径）：
  // 12px 字号、32px 行高、单元格零内边距、#D9D9D9 边框。
  // 认证/付款详情页不在 .content-main-box 内吃不到全局规则，须自带；
  // 表头底色按需求固定 #f0f0f0（全局是 #F2F2F2），加 !important 防宿主主题盖掉。
  ::v-deep .el-table {
    font-size: 12px;
    color: #212121;

    .el-table__cell {
      padding: 0;
    }

    th.el-table__cell {
      height: 32px;
      background-color: #f0f0f0 !important;
      border-right: 1px solid #d9d9d9;
      border-top: 1px solid #d9d9d9;

      &:last-child {
        border-right: none;
      }

      &:nth-last-child(2) {
        border-right: none;
      }
    }

    .el-table__body td.el-table__cell {
      height: 32px;
      border-bottom: 1px solid #d9d9d9;
    }
  }
}
</style>
